import {DateTime} from 'luxon';
import Papa from 'papaparse';
import {sanitizeFilename} from '../helpers';
import i18n from '../i18n/i18n';
import {getSpeedMultiplier, getSpeedUnit} from './measurementService';

const getReportName = ({
  teamName,
  reportName,
}: {
  reportName: string;
  teamName?: string;
}) => {
  return sanitizeFilename(
    `HELIOS${teamName ? `-${teamName}` : ''}-${reportName}-${DateTime.local().toFormat('yyyy-MM-dd-HH-mm')}.csv`,
  );
};

const downloadReportCsv = ({
  exportReportData,
  teamName,
  reportName,
}: {
  exportReportData: object[];
  teamName?: string;
  reportName: string;
}) => {
  const csv = Papa.unparse(exportReportData);
  const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute(
    'download',
    getReportName({
      teamName,
      reportName,
    }),
  );
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Helper to get position display string
const getPositionString = (position?: string | null): string | null => {
  if (!position) return null;
  return i18n.t(`position.${position}`) as string;
};

// Interface for player session export data from GraphQL
export interface PlayerSessionExportData {
  id?: number;
  type?: string;
  startTime?: string;
  endTime?: string;
  localDisplayStartTime?: string;
  activeTime?: number;
  timeOnIce?: number;
  strintTotalTime?: number;
  energyRatio?: number;
  strideCountLeft?: number;
  strideCountRight?: number;
  speedAvgV2?: number;
  speedMaxV2?: number;
  accelAvgV2?: number;
  accelMaxV2?: number;
  strintFilteredStrideRateAvgV2?: number;
  strintFilteredStrideRateMaxV2?: number;
  balanceNormalized?: number;
  cumulative3DPlayerLoadV2?: number;
  hrAvg?: number;
  hrMax?: number;
  hustleScoreTopLevel?: number;
  position?: string;
  timeZone?: string;
  teamSessionId?: number;
  firstName?: string;
  lastName?: string;
  profilePosition?: string;
  playerNumber?: number;
  playerSessionShifts?: PlayerSessionShiftExportData[];
}

export interface PlayerSessionShiftExportData {
  id?: number;
  start?: number;
  end?: number;
  localDisplayStartTime?: string;
  startTime?: string;
  endTime?: string;
  valid?: boolean;
  timeOnIce?: number;
  strintTotalTime?: number;
  energyRatio?: number;
  strideCountTotal?: number;
  strideCountLeft?: number;
  strideCountRight?: number;
  speedAvgV2?: number;
  speedMaxV2?: number;
  accelAvgV2?: number;
  accelMaxV2?: number;
  strintFilteredStrideRateAvgV2?: number;
  strintFilteredStrideRateMaxV2?: number;
  balanceNormalized?: number;
  hrAvg?: number;
  hrMax?: number;
  hrStart?: number;
  hrEnd?: number;
  cumulative3DPlayerLoadV2?: number;
}

// Format player session data for CSV export
const formatPlayerSessionExportData = (
  playerSession: PlayerSessionExportData,
  preferredUnits: string = 'imperial',
): Record<string, string | number | null> => {
  const isMetric = preferredUnits === 'metric';
  const speedMultiplier = getSpeedMultiplier(isMetric);
  const speedUnit = getSpeedUnit(isMetric);
  const t = i18n.t;

  const position = getPositionString(
    playerSession.profilePosition || playerSession.position,
  );

  // Calculate load per minute: (cumulative3DPlayerLoadV2 / 100) / (activeTime / 60)
  let loadPerMinute: number | null = null;
  if (
    playerSession.cumulative3DPlayerLoadV2 != null &&
    playerSession.activeTime != null &&
    playerSession.activeTime > 0
  ) {
    const playerLoad = playerSession.cumulative3DPlayerLoadV2 / 100;
    const activeTimeMinutes = playerSession.activeTime / 60;
    loadPerMinute = Math.round(playerLoad / activeTimeMinutes);
  }

  return {
    [t('profile.firstName')]: playerSession.firstName || null,
    [t('profile.lastName')]: playerSession.lastName || null,
    [t('profile.position')]: position,
    [t('profile.number')]: playerSession.playerNumber ?? null,
    [t('export.teamSessionId')]: playerSession.teamSessionId ?? null,
    [t('sessionStats.sessionType')]: playerSession.type
      ? playerSession.type.charAt(0).toUpperCase() + playerSession.type.slice(1)
      : null,
    [t('sessionStats.localDisplayStartTime')]:
      playerSession.localDisplayStartTime || null,
    [t('sessionStats.startTime') + ' (UTC)']: playerSession.startTime
      ? DateTime.fromISO(playerSession.startTime).toUTC().toISO()
      : null,
    [t('sessionStats.endTime') + ' (UTC)']: playerSession.endTime
      ? DateTime.fromISO(playerSession.endTime).toUTC().toISO()
      : null,
    [t('teamSessionPlayerList.activeTime') + ' (min)']:
      playerSession.activeTime != null
        ? Number((playerSession.activeTime / 60).toFixed(2))
        : null,
    [t('teamSessionPlayerList.strintTotalTime') + ' (min)']:
      playerSession.strintTotalTime != null
        ? Number((playerSession.strintTotalTime / 60).toFixed(2))
        : null,
    [t('teamSessionPlayerList.energyRatio') + ' (%)']:
      playerSession.energyRatio != null
        ? parseFloat((playerSession.energyRatio * 100).toFixed(1))
        : null,
    [t('teamSessionPlayerList.hustleScore')]:
      playerSession.hustleScoreTopLevel ?? null,
    [t('sessionStats.strides')]:
      (playerSession.strideCountLeft ?? 0) +
        (playerSession.strideCountRight ?? 0) || null,
    [t('teamSessionPlayerList.strideCountLeft')]:
      playerSession.strideCountLeft ?? null,
    [t('teamSessionPlayerList.strideCountRight')]:
      playerSession.strideCountRight ?? null,
    [`${t('teamSessionPlayerList.speedAvg', {units: speedUnit})}`]:
      playerSession.speedAvgV2 != null
        ? parseFloat((playerSession.speedAvgV2 * speedMultiplier).toFixed(1))
        : null,
    [`${t('teamSessionPlayerList.speedMax', {units: speedUnit})}`]:
      playerSession.speedMaxV2 != null
        ? parseFloat((playerSession.speedMaxV2 * speedMultiplier).toFixed(1))
        : null,
    [t('teamSessionPlayerList.accelAvg')]:
      playerSession.accelAvgV2 != null
        ? parseFloat((playerSession.accelAvgV2 / 9.8).toFixed(2))
        : null,
    [t('teamSessionPlayerList.accelMax')]:
      playerSession.accelMaxV2 != null
        ? parseFloat((playerSession.accelMaxV2 / 9.8).toFixed(2))
        : null,
    [t('teamSessionPlayerList.agilityAvg', {units: 'spm'})]:
      playerSession.strintFilteredStrideRateAvgV2 != null
        ? Math.round(playerSession.strintFilteredStrideRateAvgV2)
        : null,
    [t('teamSessionPlayerList.agilityMax', {units: 'spm'})]:
      playerSession.strintFilteredStrideRateMaxV2 != null
        ? Math.round(playerSession.strintFilteredStrideRateMaxV2)
        : null,
    [t('teamSessionPlayerList.balanceNormalized') + ' (%)']:
      playerSession.balanceNormalized != null
        ? parseFloat(((playerSession.balanceNormalized / 0.5) * 100).toFixed(1))
        : null,
    [t('teamSessionPlayerList.playerLoad')]:
      playerSession.cumulative3DPlayerLoadV2 != null
        ? Math.round(playerSession.cumulative3DPlayerLoadV2 / 100)
        : null,
    [t('statistics.loadPerMinute')]: loadPerMinute,
    [t('teamSessionPlayerList.hrAvg')]: playerSession.hrAvg ?? null,
    [t('teamSessionPlayerList.hrMax')]: playerSession.hrMax ?? null,
  };
};

// Format shift data for CSV export
const formatShiftExportData = (
  shift: PlayerSessionShiftExportData & {
    name?: string;
    position?: string;
    type?: string;
    timeZone?: string;
    teamSessionId?: number;
  },
  preferredUnits: string = 'imperial',
): Record<string, string | number | null> => {
  const isMetric = preferredUnits === 'metric';
  const speedMultiplier = getSpeedMultiplier(isMetric);
  const speedUnit = getSpeedUnit(isMetric);
  const t = i18n.t;

  // Calculate load per minute for shift: (cumulative3DPlayerLoadV2 / 100) / ((end - start) / 60)
  let loadPerMinute: number | null = null;
  if (
    shift.cumulative3DPlayerLoadV2 != null &&
    shift.end != null &&
    shift.start != null &&
    shift.end - shift.start > 0
  ) {
    const playerLoad = shift.cumulative3DPlayerLoadV2 / 100;
    const shiftTimeMinutes = (shift.end - shift.start) / 60;
    loadPerMinute = Math.round(playerLoad / shiftTimeMinutes);
  }

  return {
    [t('general.name')]: shift.name || null,
    [t('profile.position')]: getPositionString(shift.position),
    [t('export.teamSessionId')]: shift.teamSessionId ?? null,
    [t('sessionStats.sessionType')]: shift.type
      ? shift.type.charAt(0).toUpperCase() + shift.type.slice(1)
      : null,
    [t('sessionStats.localDisplayStartTime')]:
      shift.localDisplayStartTime || null,
    [t('sessionStats.startTime') + ' (UTC)']: shift.startTime
      ? DateTime.fromISO(shift.startTime).toUTC().toISO()
      : null,
    [t('sessionStats.endTime') + ' (UTC)']: shift.endTime
      ? DateTime.fromISO(shift.endTime).toUTC().toISO()
      : null,
    [t('shifts.timeAbbrev') + ' (min)']:
      shift.end != null && shift.start != null
        ? Number(((shift.end - shift.start) / 60).toFixed(2))
        : null,
    [t('teamSessionPlayerList.timeOnIce') + ' (min)']:
      shift.timeOnIce != null
        ? Number((shift.timeOnIce / 60).toFixed(2))
        : null,
    [t('teamSessionPlayerList.strintTotalTime') + ' (min)']:
      shift.strintTotalTime != null
        ? Number((shift.strintTotalTime / 60).toFixed(2))
        : null,
    [t('teamSessionPlayerList.energyRatio') + ' (%)']:
      shift.energyRatio != null
        ? parseFloat((shift.energyRatio * 100).toFixed(1))
        : null,
    [t('sessionStats.strides')]: shift.strideCountTotal ?? null,
    [t('teamSessionPlayerList.strideCountLeft')]: shift.strideCountLeft ?? null,
    [t('teamSessionPlayerList.strideCountRight')]:
      shift.strideCountRight ?? null,
    [`${t('teamSessionPlayerList.speedAvg', {units: speedUnit})}`]:
      shift.speedAvgV2 != null
        ? parseFloat((shift.speedAvgV2 * speedMultiplier).toFixed(1))
        : null,
    [`${t('teamSessionPlayerList.speedMax', {units: speedUnit})}`]:
      shift.speedMaxV2 != null
        ? parseFloat((shift.speedMaxV2 * speedMultiplier).toFixed(1))
        : null,
    [t('teamSessionPlayerList.accelAvg')]:
      shift.accelAvgV2 != null
        ? parseFloat((shift.accelAvgV2 / 9.8).toFixed(2))
        : null,
    [t('teamSessionPlayerList.accelMax')]:
      shift.accelMaxV2 != null
        ? parseFloat((shift.accelMaxV2 / 9.8).toFixed(2))
        : null,
    [t('teamSessionPlayerList.agilityAvg', {units: 'spm'})]:
      shift.strintFilteredStrideRateAvgV2 != null
        ? Math.round(shift.strintFilteredStrideRateAvgV2)
        : null,
    [t('teamSessionPlayerList.agilityMax', {units: 'spm'})]:
      shift.strintFilteredStrideRateMaxV2 != null
        ? Math.round(shift.strintFilteredStrideRateMaxV2)
        : null,
    [t('teamSessionPlayerList.balanceNormalized') + ' (%)']:
      shift.balanceNormalized != null
        ? parseFloat(((shift.balanceNormalized / 0.5) * 100).toFixed(1))
        : null,
    [t('teamSessionPlayerList.hrAvg')]: shift.hrAvg ?? null,
    [t('teamSessionPlayerList.hrMax')]: shift.hrMax ?? null,
    [t('shifts.heartRateShiftStart')]: shift.hrStart ?? null,
    [t('shifts.heartRateShiftEnd')]: shift.hrEnd ?? null,
    [t('teamSessionPlayerList.playerLoad')]:
      shift.cumulative3DPlayerLoadV2 != null
        ? Math.round(shift.cumulative3DPlayerLoadV2 / 100)
        : null,
    [t('statistics.loadPerMinute')]: loadPerMinute,
  };
};

// Aggregate multiple player sessions into a single row (for team session totals)
const aggregatePlayerSessionExportData = (
  sessions: PlayerSessionExportData[],
  preferredUnits: string = 'imperial',
): Record<string, string | number | null> => {
  if (sessions.length === 0) return {};

  const isMetric = preferredUnits === 'metric';
  const speedMultiplier = getSpeedMultiplier(isMetric);
  const speedUnit = getSpeedUnit(isMetric);
  const t = i18n.t;

  const firstSession = [...sessions].sort((a, b) =>
    (a.startTime || '').localeCompare(b.startTime || ''),
  )[0];

  const position = getPositionString(
    firstSession.profilePosition || firstSession.position,
  );

  // Helper functions
  const sum = (values: (number | null | undefined)[]) =>
    values.filter((v): v is number => v != null).reduce((a, b) => a + b, 0);

  const avg = (values: (number | null | undefined)[]) => {
    const filtered = values.filter((v): v is number => v != null);
    return filtered.length ? sum(filtered) / filtered.length : null;
  };

  const max = (values: (number | null | undefined)[]) => {
    const filtered = values.filter((v): v is number => v != null);
    return filtered.length ? Math.max(...filtered) : null;
  };

  const activeTimes = sessions.map(s => s.activeTime);
  const timeOnIces = sessions.map(s => s.timeOnIce);
  const strideTimes = sessions.map(s => s.strintTotalTime);
  const playerLoads = sessions.map(s =>
    s.cumulative3DPlayerLoadV2 != null
      ? s.cumulative3DPlayerLoadV2 / 100
      : null,
  );

  const totalActiveTime = sum(activeTimes);
  const totalTimeOnIce = sum(timeOnIces);
  const totalPlayerLoad = sum(playerLoads);

  // Calculate load per minute
  let loadPerMinute: number | null = null;
  if (totalPlayerLoad > 0 && totalActiveTime > 0) {
    loadPerMinute = Math.round(totalPlayerLoad / (totalActiveTime / 60));
  }

  return {
    [t('profile.firstName')]: firstSession.firstName || null,
    [t('profile.lastName')]: firstSession.lastName || null,
    [t('profile.position')]: position,
    [t('profile.number')]: firstSession.playerNumber ?? null,
    [t('export.teamSessionId')]: firstSession.teamSessionId ?? null,
    [t('sessionStats.sessionType')]: firstSession.type
      ? firstSession.type.charAt(0).toUpperCase() + firstSession.type.slice(1)
      : null,
    [t('sessionStats.localDisplayStartTime')]:
      firstSession.localDisplayStartTime || null,
    [t('sessionStats.startTime') + ' (UTC)']: firstSession.startTime
      ? DateTime.fromISO(firstSession.startTime).toUTC().toISO()
      : null,
    [t('teamSessionPlayerList.activeTime') + ' (min)']:
      totalActiveTime > 0 ? Number((totalActiveTime / 60).toFixed(2)) : null,
    [t('teamSessionPlayerList.timeOnIce') + ' (min)']:
      totalTimeOnIce > 0 ? Number((totalTimeOnIce / 60).toFixed(2)) : null,
    [t('teamSessionPlayerList.strintTotalTime') + ' (min)']:
      sum(strideTimes) > 0 ? Number((sum(strideTimes) / 60).toFixed(2)) : null,
    [t('teamSessionPlayerList.energyRatio') + ' (%)']:
      avg(sessions.map(s => s.energyRatio)) != null
        ? parseFloat((avg(sessions.map(s => s.energyRatio))! * 100).toFixed(1))
        : null,
    [t('teamSessionPlayerList.hustleScore')]:
      avg(sessions.map(s => s.hustleScoreTopLevel)) != null
        ? Math.round(avg(sessions.map(s => s.hustleScoreTopLevel))!)
        : null,
    [t('sessionStats.strides')]:
      sum(
        sessions.map(s => (s.strideCountLeft ?? 0) + (s.strideCountRight ?? 0)),
      ) || null,
    [t('teamSessionPlayerList.strideCountLeft')]:
      sum(sessions.map(s => s.strideCountLeft)) || null,
    [t('teamSessionPlayerList.strideCountRight')]:
      sum(sessions.map(s => s.strideCountRight)) || null,
    [`${t('teamSessionPlayerList.speedAvg', {units: speedUnit})}`]:
      avg(sessions.map(s => s.speedAvgV2)) != null
        ? parseFloat(
            (avg(sessions.map(s => s.speedAvgV2))! * speedMultiplier).toFixed(
              1,
            ),
          )
        : null,
    [`${t('teamSessionPlayerList.speedMax', {units: speedUnit})}`]:
      max(sessions.map(s => s.speedMaxV2)) != null
        ? parseFloat(
            (max(sessions.map(s => s.speedMaxV2))! * speedMultiplier).toFixed(
              1,
            ),
          )
        : null,
    [t('teamSessionPlayerList.accelAvg')]:
      avg(sessions.map(s => s.accelAvgV2)) != null
        ? parseFloat((avg(sessions.map(s => s.accelAvgV2))! / 9.8).toFixed(2))
        : null,
    [t('teamSessionPlayerList.accelMax')]:
      max(sessions.map(s => s.accelMaxV2)) != null
        ? parseFloat((max(sessions.map(s => s.accelMaxV2))! / 9.8).toFixed(2))
        : null,
    [t('teamSessionPlayerList.agilityAvg', {units: 'spm'})]:
      avg(sessions.map(s => s.strintFilteredStrideRateAvgV2)) != null
        ? Math.round(avg(sessions.map(s => s.strintFilteredStrideRateAvgV2))!)
        : null,
    [t('teamSessionPlayerList.agilityMax', {units: 'spm'})]:
      max(sessions.map(s => s.strintFilteredStrideRateMaxV2)) != null
        ? Math.round(max(sessions.map(s => s.strintFilteredStrideRateMaxV2))!)
        : null,
    [t('teamSessionPlayerList.balanceNormalized') + ' (%)']:
      avg(sessions.map(s => s.balanceNormalized)) != null
        ? parseFloat(
            (
              (avg(sessions.map(s => s.balanceNormalized))! / 0.5) *
              100
            ).toFixed(1),
          )
        : null,
    [t('teamSessionPlayerList.playerLoad')]:
      totalPlayerLoad > 0 ? Math.round(totalPlayerLoad) : null,
    [t('statistics.loadPerMinute')]: loadPerMinute,
    [t('teamSessionPlayerList.hrAvg')]:
      avg(sessions.map(s => s.hrAvg)) != null
        ? Math.round(avg(sessions.map(s => s.hrAvg))!)
        : null,
    [t('teamSessionPlayerList.hrMax')]:
      max(sessions.map(s => s.hrMax)) != null
        ? Math.round(max(sessions.map(s => s.hrMax))!)
        : null,
  };
};

export {
  getReportName,
  downloadReportCsv,
  getPositionString,
  formatPlayerSessionExportData,
  formatShiftExportData,
  aggregatePlayerSessionExportData,
};
